import * as XLSX from "xlsx";
import type { ParsedCsv } from "./csv";

/**
 * Client-side XLSX parsing for `POST admin/contacts/import` (epic #236 task
 * #238, work unit 3): produces the exact same `{headers, rows}` shape
 * `parseCsv` produces, so `buildImportRows`/`ColumnMapping` in `csv.ts` work
 * unchanged regardless of which file type was uploaded. Only the FIRST sheet
 * is read — this task has no multi-sheet requirement anywhere in scope.
 */

/**
 * `raw: false` (not the default `raw: true`) makes SheetJS return each
 * cell's DISPLAYED text instead of its underlying JS value — a phone number
 * typed into a spreadsheet cell must come back as the string "3001234567",
 * never the JS number `3001234567`, since every `AdminContactImportRow`
 * field is a string server-side and a raw number would silently strip
 * leading zeros or add unwanted decimal formatting.
 *
 * `header: 1` asks for an array-of-arrays instead of the default
 * array-of-objects-keyed-by-header-row — the same header/rows split
 * `parseCsv` already returns. A short row (trailing blank cells SheetJS
 * omits) is left as-is rather than padded to the header's length:
 * `buildImportRows` already guards every `row[index]` read with an
 * `undefined` check (see csv.ts), so a missing trailing cell behaves
 * identically to an empty CSV cell without needing padding here.
 *
 * `blankrows: false` (Codex finding, PR #248): without it, `defval: ""`
 * turns an empty separator row into a real all-empty-string row instead of
 * dropping it, inflating the detected row count and producing a spurious
 * "email_requerido" rejection per blank row — `parseCsv` already filters
 * blank lines (`.filter((line) => line.length > 0)`), so this keeps both
 * parsers behaving the same way for a file that merely has visual spacing.
 */
export function parseXlsx(buffer: ArrayBuffer): ParsedCsv {
  const workbook = XLSX.read(buffer, { type: "array" });
  const firstSheetName = workbook.SheetNames[0];
  if (firstSheetName === undefined) {
    return { headers: [], rows: [] };
  }

  const sheet = workbook.Sheets[firstSheetName];
  // Codex round 6 (PR #386): read WITH blank rows and drop them here so each
  // surviving row keeps its real worksheet row number (`blankrows: false`
  // discards that position). A row is blank when every cell is "".
  const table = XLSX.utils.sheet_to_json<string[]>(sheet ?? {}, { header: 1, raw: false, defval: "", blankrows: true });
  const firstSourceRow = sheet?.["!ref"] ? XLSX.utils.decode_range(sheet["!ref"]).s.r + 1 : 1;

  const kept = table
    .map((row, index) => ({ row, sourceRow: firstSourceRow + index }))
    .filter(({ row }) => row.some((cell) => cell !== ""));

  const [headerEntry, ...dataEntries] = kept;
  return {
    headers: headerEntry?.row ?? [],
    rows: dataEntries.map(({ row }) => row),
    sourceRows: dataEntries.map(({ sourceRow }) => sourceRow),
  };
}
